"use client";

import { AudioInterview } from "@/components/modules/audio-interview/audio-interview";
import { CandidateInterviewComponent } from "@/components/modules/heygen/interview";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useEnv } from "@/context/envContext";
import { useDebounce } from "@/hooks/use-debounce";
import { toast } from "@/hooks/use-toast";
import { clearAudioPaths } from "@/lib/audio-storage";
import { RecJob } from "@/lib/types/job.types";
import { ProbeRequest, ProbeResult } from "@/lib/types/probe.types";
import { getApplicantEmailAbuseMessage } from "@/lib/validation/applicant-email";
import {
  getApplicantNameAbuseMessage,
  normalizeApplicantName,
} from "@/lib/validation/applicant-name";
import { customFetch } from "@/utils/api";
import {
  ArrowLeft,
  ArrowRight,
  Clock,
  Loader2,
  MapPin,
  Upload
} from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { CandidateFeedbackProbeComponent } from "../../career/candidate-probe/candidate-feedback-probe";

function InterviewModeGate({
  probeConfig,
  jobId,
  csrfToken,
  onComplete,
  applicationId,
}: {
  probeConfig: Omit<ProbeRequest, "message" | "chat_history">;
  jobId: string;
  csrfToken: string;
  onComplete?: (result: ProbeResult) => void;
  applicationId?: string;
}) {
  const [mode, setMode] = useState<"avatar" | "audio" | null>(null);
  const [open, setOpen] = useState<boolean>(true);

  return (
    <>
      <Dialog open={open && mode === null} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Choose interview mode</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Button
              onClick={() => {
                setMode("avatar");
                setOpen(false);
              }}
              className="h-12"
            >
              Avatar
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setMode("audio");
                setOpen(false);
              }}
              className="h-12"
            >
              Audio only
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {mode === "avatar" && (
        <CandidateInterviewComponent
          probeConfig={probeConfig}
          jobId={jobId}
          onComplete={onComplete}
          csrfToken={csrfToken}
          applicationId={applicationId}
        />
      )}
      {mode === "audio" && (
        <AudioInterview
          probeConfig={probeConfig}
          jobId={jobId}
          onComplete={onComplete}
          csrfToken={csrfToken}
          applicationId={applicationId}
        />
      )}
    </>
  );
}

interface QuestionAnswer {
  questionId: string;
  answer: {
    type: string;
    value: string | string[];
  };
}

interface RecJobQuestion {
  id: string;
  question: string;
  type: string;
  isRequired: boolean;
  possibleAnswers?: string[];
}

interface CSRFError {
  statusCode: number;
  message: string;
  error: string;
}

const readFormValue = (id: string): string =>
  (document.getElementById(id) as HTMLInputElement | null)?.value.trim() ?? "";

// Add this function to validate filenames for unsafe characters
const validateFileName = (fileName: string): boolean => {
  // Check for potentially dangerous patterns in filenames
  const unsafePatterns = [
    "../", // Directory traversal
    "..\\", // Windows directory traversal
    "%", // URL encoding
    "\\", // Backslash
    "&", // Special characters
    "<", // HTML tags
    ">", // HTML tags
    ";", // Command injection
    "|", // Pipe
    "$", // Shell variables
    "`", // Command substitution
    "!", // Shell history
    "{", // Expansion
    "}", // Expansion
    "[", // Globbing
    "]", // Globbing
    ":", // Path separator in some systems
    "*", // Wildcard
    "?", // Wildcard
    '"', // Quotes
    "'", // Quotes
    "\0", // Null byte
  ];

  return !unsafePatterns.some((pattern) => fileName.includes(pattern));
};

// Enhanced function to validate file content type using magic bytes
const validateFileType = async (file: File): Promise<boolean> => {
  return new Promise((resolve) => {
    // Read more bytes for a more comprehensive check
    const bytesToRead = 1024; // Read first 1KB to capture more of the file structure
    const reader = new FileReader();

    reader.onloadend = (e) => {
      if (!e.target?.result) {
        console.error("File reading failed: No result available");
        resolve(false);
        return;
      }

      try {
        const buffer = e.target.result as ArrayBuffer;
        const arr = new Uint8Array(buffer);

        // Convert first 8 bytes to hex string for more comprehensive checking
        let header = "";
        for (let i = 0; i < Math.min(8, arr.length); i++) {
          // Ensure two-digit hex representation with leading zero if needed
          header += arr[i].toString(16).padStart(2, "0");
        }

        // PDF signature validation:
        // 1. Check for standard PDF header: %PDF- (25 50 44 46 2D)
        const hasPdfHeader = header.toLowerCase().startsWith("255044462d");

        // 2. Additional validation: Check for PDF version pattern after header
        // Common versions are 1.0 to 1.7, represented as: %PDF-1.X
        // We'll check if byte 6 is '31' (ASCII '1') and byte 7 is '2e' (ASCII '.')
        const hasValidVersion =
          arr.length >= 8 &&
          arr[5] === 0x31 && // '1'
          arr[6] === 0x2e; // '.'

        // 3. Look for %%EOF marker somewhere in the first 1KB
        // This is a common marker at the end of PDF files, but some valid PDFs
        // might not have this in the first 1KB if they're large
        let hasEofMarker = false;
        const content = new TextDecoder().decode(arr);
        if (content.includes("%%EOF")) {
          hasEofMarker = true;
        }

        // For a file to be considered a valid PDF, it must have the PDF header
        // and at least one of the other validation checks should pass
        const isPDF = hasPdfHeader && (hasValidVersion || hasEofMarker);

        if (!isPDF) {
          console.warn("PDF validation failed:", {
            hasPdfHeader,
            hasValidVersion,
            hasEofMarker,
            headerHex: header,
          });
        }

        resolve(isPDF);
      } catch (error) {
        console.error("Error validating file type:", error);
        resolve(false);
      }
    };

    reader.onerror = (error) => {
      console.error("File reading error:", error);
      resolve(false);
    };

    // Read the beginning of the file
    reader.readAsArrayBuffer(file.slice(0, bytesToRead));
  });
};

// Add IDB helper functions
const IDB_NAME = "job_applications";
const IDB_VERSION = 2; // Increment version number to trigger onupgradeneeded

const initDB = async () => {
  return new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(IDB_NAME, IDB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      // Create forms store if it doesn't exist
      if (!db.objectStoreNames.contains("forms")) {
        db.createObjectStore("forms", { keyPath: "jobId" });
      }
      // Create probes store if it doesn't exist
      if (!db.objectStoreNames.contains("probes")) {
        db.createObjectStore("probes", { keyPath: "jobId" });
      }
    };
  });
};

// Update SavedFormData interface to include resume and resumeId
interface SavedFormData {
  jobId: string;
  formData: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    city: string;
    province: string;
    country: string;
    linkedin: string;
    dateAvailable: string;
  };
  answers: QuestionAnswer[];
  lastUpdated: string;
  resume?: {
    file: File | null;
    fileName: string | null;
  };
  resumeId?: string; // Add resumeId field
}

// Initial form state constant
const INITIAL_FORM_STATE = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  city: "", // Empty string
  province: "", // Empty string
  country: "indonesia", // Always Indonesia
  linkedin: "",
  dateAvailable: "",
};

// Update loadFromIDB to handle resume
const loadFromIDB = async (jobId: string): Promise<SavedFormData | null> => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(["forms"], "readonly");
    const store = transaction.objectStore("forms");
    const request = store.get(jobId);

    request.onerror = () => {
      console.error("Error loading from IDB:", request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      const data = request.result;

      if (data?.resume?.file) {
        try {
          // Convert ArrayBuffer back to File
          const file = new File(
            [data.resume.file],
            data.resume.fileName || "resume.pdf",
            {
              type: "application/pdf",
            },
          );
          data.resume.file = file;
        } catch (error) {
          console.error("Error reconstructing resume file:", error);
          data.resume = undefined;
        }
      }
      resolve(data);
    };
  });
};

// Update the save to IDB function to handle resume
const saveToIDB = async (
  jobId: string,
  data: {
    formData: SavedFormData["formData"];
    answers: QuestionAnswer[];
    resume?: {
      file: File;
      fileName: string;
    };
    resumeId?: string;
  },
) => {
  try {
    const db = await initDB();

    // First, get existing data to preserve resume if it exists
    const existingData = await new Promise<SavedFormData | undefined>(
      (resolve) => {
        const transaction = db.transaction(["forms"], "readonly");
        const store = transaction.objectStore("forms");
        const request = store.get(jobId);

        request.onsuccess = () => resolve(request.result);
        request.onerror = () => resolve(undefined);
      },
    );

    // Prepare resume data
    let resumeData = existingData?.resume; // Keep existing resume by default
    if (data.resume?.file) {
      // Only update resume if new one is provided
      try {
        const arrayBuffer = await data.resume.file.arrayBuffer();
        resumeData = {
          file: new File([arrayBuffer], data.resume.fileName, {
            type: "application/pdf",
          }),
          fileName: data.resume.fileName,
        };
      } catch (error) {
        console.error("Error converting resume to ArrayBuffer:", error);
      }
    }

    // Now create a new transaction for saving
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(["forms"], "readwrite");
      const store = transaction.objectStore("forms");

      const saveData = {
        jobId,
        formData: data.formData,
        answers: data.answers,
        lastUpdated: new Date().toISOString(),
        resume: resumeData, // Include existing or new resume data
        resumeId: data.resumeId || existingData?.resumeId, // Include resumeId from data or keep existing one
      };

      const request = store.put(saveData);

      request.onerror = () => {
        console.error("Error in IDB request:", request.error);
        reject(request.error);
      };

      transaction.oncomplete = () => {
        resolve(true);
      };

      transaction.onerror = () => {
        console.error("Transaction error:", transaction.error);
        reject(transaction.error);
      };
    });
  } catch (error) {
    if (error instanceof Error && error.name === "QuotaExceededError") {
      console.error("Storage quota exceeded:", error);
      toast({
        title: "Storage Limit Exceeded",
        description:
          "Unable to save your progress due to browser storage limits. Your application will continue but progress won't be saved.",
        variant: "destructive",
        duration: 5000,
      });
    } else {
      console.error("Error saving to IndexedDB:", error);
    }
    throw error;
  }
};

// Add this skeleton component near the top of the file
const JobDetailSkeleton = () => (
  <div className="space-y-8 animate-pulse">
    {/* Header Card Skeleton */}
    <Card className="mb-8">
      <CardHeader className="bg-gradient-to-r from-gray-200 to-gray-300 h-32">
        <div className="space-y-4">
          <div className="h-8 w-2/3 bg-gray-300 rounded"></div>
          <div className="h-6 w-1/3 bg-gray-300 rounded"></div>
        </div>
      </CardHeader>
    </Card>

    {/* Content Sections Skeleton */}
    <div className="space-y-8">
      {/* About First Section */}
      <section>
        <div className="h-8 w-1/4 bg-gray-200 rounded mb-4"></div>
        <div className="space-y-4">
          <div className="h-4 bg-gray-200 rounded"></div>
          <div className="h-4 bg-gray-200 rounded"></div>
          <div className="h-4 w-3/4 bg-gray-200 rounded"></div>
        </div>
      </section>

      {/* About You Section */}
      <section>
        <div className="h-8 w-1/4 bg-gray-200 rounded mb-4"></div>
        <div className="space-y-4">
          <div className="h-4 bg-gray-200 rounded"></div>
          <div className="h-4 bg-gray-200 rounded"></div>
          <div className="h-4 w-3/4 bg-gray-200 rounded"></div>
        </div>
      </section>

      {/* About Role Section */}
      <section>
        <div className="h-8 w-1/4 bg-gray-200 rounded mb-4"></div>
        <div className="space-y-4">
          <div className="h-4 bg-gray-200 rounded"></div>
          <div className="h-4 bg-gray-200 rounded"></div>
          <div className="h-4 w-3/4 bg-gray-200 rounded"></div>
        </div>
      </section>
    </div>
  </div>
);

const calculateEstimatedTime = (
  generalQuestionsCount: number,
  specificQuestionsCount: number,
  hasProbe: boolean,
): { minutes: number; breakdown: { [key: string]: number } } => {
  const breakdown = {
    basicInfo: 0.5, // 30 seconds for basic information
    generalQuestions: generalQuestionsCount * 0.5, // 30 seconds per general question
    specificQuestions: specificQuestionsCount * 1, // 1 minutes per specific question
    probe: hasProbe ? 10 : 0, // 10 minutes for probe if it exists
  };

  const totalMinutes = Object.values(breakdown).reduce((a, b) => a + b, 0);

  return {
    minutes: totalMinutes,
    breakdown,
  };
};

// Add this interface near the top with other interfaces
interface TnCContent {
  contentId: string;
  pageType: string;
  sectionKey: string;
  content: {
    text: string;
  };
  style: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  filePath: string;
}

const FIRST_THINGS_FIRST_STATEMENTS = [
  "I take ownership of my work and strive for meaningful impact",
  "I embrace change and seek continuous growth, even when it's uncomfortable",
  "I adapt quickly in fast-moving and ambiguous environments",
  "I get things done efficiently and can deliver results at a fast pace",
  "I collaborate openly, respect diverse perspectives, and value teamwork",
  "I always put customers first and aim to create a better experience for them",
  "I uphold integrity, take responsibility, and learn from mistakes",
] as const;

const TNC_SCROLL_BOTTOM_THRESHOLD_PX = 16;

const formatTncHtml = (content: string) =>
  content
    .replace(/\n/g, "<br/>")
    .replace(
      /<h([1-6])>/g,
      '<h$1 class="text-lg md:text-xl font-semibold text-[#164a7a] mt-4 md:mt-6 mb-2 md:mb-4">',
    )
    .replace(/<p>/g, '<p class="text-gray-700 mb-3 md:mb-4 text-sm md:text-base">')
    .replace(
      /<ul>/g,
      '<ul class="list-disc pl-4 md:pl-6 mb-3 md:mb-4 space-y-1 md:space-y-2">',
    )
    .replace(
      /<ol>/g,
      '<ol class="list-decimal pl-4 md:pl-6 mb-3 md:mb-4 space-y-1 md:space-y-2">',
    )
    .replace(/<li>/g, '<li class="text-gray-700 text-sm md:text-base">')
    .replace(/<strong>/g, '<strong class="font-semibold text-[#164a7a]">')
    .replace(
      /<a /g,
      '<a class="text-blue-600 hover:text-blue-800 underline" ',
    );

const hasReachedTncBottom = (element: HTMLDivElement) =>
  element.scrollTop + element.clientHeight >=
  element.scrollHeight - TNC_SCROLL_BOTTOM_THRESHOLD_PX;

export function JobApplicationPageComponent({ jobId }: { jobId: string }) {
  const router = useRouter();
  const env = useEnv();
  const [showFirstThingsFirst, setShowFirstThingsFirst] = useState(false);
  const [showApplication, setShowApplication] = useState(false);
  const [firstThingsFirstComplete, setFirstThingsFirstComplete] =
    useState(false);
  const [jobDetails, setJobDetails] = useState<RecJob | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [sourceError, setSourceError] = useState<string | null>(null);
  const [requiredQuestionsError, setRequiredQuestionsError] = useState<
    string | null
  >(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isParsingResume, setIsParsingResume] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState<string>("");
  // const [cmsData, setCmsData] = useState<{ [key: string]: SectionData }>({});
  const [generalQuestions, setGeneralQuestions] = useState<RecJobQuestion[]>(
    [],
  );
  const [jobSpecificQuestions, setJobSpecificQuestions] = useState<
    RecJobQuestion[]
  >([]);
  const [answers, setAnswers] = useState<QuestionAnswer[]>([]);
  const [currentSection, setCurrentSection] = useState<
    "easyApply" | "application" | "general" | "specific"
  >("easyApply");
  const [applicationCompleted, setApplicationCompleted] = useState(false);
  const [generalQuestionsCompleted, setGeneralQuestionsCompleted] =
    useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [showProbe, setShowProbe] = useState(false);
  const [probeConfig, setProbeConfig] = useState<Omit<
    ProbeRequest,
    "message" | "chat_history"
  > | null>(null);
  const [csrfToken, setCsrfToken] = useState<string>("");
  const [capturedFormData, setCapturedFormData] = useState<{
    jobId: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    city: string;
    province: string;
    country: string;
    linkedinUrl?: string;
    resume: File | null;
    dateAvailable?: string;
    answers: QuestionAnswer[];
    resumeId?: string;
  } | null>(null);
  const [formState, setFormState] = useState<typeof INITIAL_FORM_STATE>({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    city: "", // Empty string
    province: "", // Empty string
    country: "indonesia", // Always Indonesia
    linkedin: "",
    dateAvailable: "",
  });
  const nameAbuseMessage = useMemo(
    () =>
      getApplicantNameAbuseMessage(formState.firstName, formState.lastName),
    [formState.firstName, formState.lastName],
  );
  const emailAbuseMessage = useMemo(
    () => getApplicantEmailAbuseMessage(formState.email),
    [formState.email],
  );
  const isApplyBlocked = nameAbuseMessage !== null || emailAbuseMessage !== null;
  const [showSavedDataDialog, setShowSavedDataDialog] = useState(false);
  const [savedData, setSavedData] = useState<SavedFormData | null>(null);
  const [hasExistingProbe, setHasExistingProbe] = useState(false);
  const [categoryData, setCategoryData] = useState<string | null>(null);
  const [sectionData, setSectionData] = useState<string | null>(null);
  const [showResumeUploadError, setShowResumeUploadError] = useState(false);
  const [tncAccepted, setTncAccepted] = useState(false);
  const [tncContent, setTncContent] = useState<string>("");
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [firstThingsFirstChecks, setFirstThingsFirstChecks] = useState<
    boolean[]
  >(() => FIRST_THINGS_FIRST_STATEMENTS.map(() => false));
  const tncScrollRef = useRef<HTMLDivElement>(null);
  const [formDataToSubmit, setFormDataToSubmit] = useState<FormData | null>(
    null,
  );
  const [interviewPreference, setInterviewPreference] = useState<
    "IMMEDIATE" | "POSTPONE" | null
  >(null);
  const [showProbeSelection, setShowProbeSelection] = useState(false);
  const [showSubmissionLoading, setShowSubmissionLoading] = useState(false);
  const [submissionProgress, setSubmissionProgress] = useState(0);
  const [interviewPreferenceLoading, setInterviewPreferenceLoading] =
    useState(false);
  const [candidateSources, setCandidateSources] = useState<
    Array<{
      id: string;
      name: string;
      description: string | null;
      displayOrder: number;
      isActive: boolean;
    }>
  >([]);
  const [selectedSource, setSelectedSource] = useState<string>("");
  const [otherSourceText, setOtherSourceText] = useState<string>("");

  // Remove the hardcoded isProbe constant and use job details
  const isProbe = jobDetails?.useLlmProbing ?? false;

  // Debounce the form state
  const debouncedFormState = useDebounce(formState, 500);

  // Helper function to check if saved data is different from initial state
  const hasDifferentValues = (savedData: SavedFormData): boolean => {
    // Check if form data has any non-empty values
    const hasFormData = Object.entries(savedData.formData).some(
      ([key, value]) => {
        const initialValue =
          INITIAL_FORM_STATE[key as keyof typeof INITIAL_FORM_STATE];
        return value !== initialValue;
      },
    );

    // Check if there are any answers
    const hasAnswers = savedData.answers && savedData.answers.length > 0;

    return hasFormData || hasAnswers;
  };

  // Fetch candidate sources on component mount
  useEffect(() => {
    const fetchCandidateSources = async () => {
      try {
        const response = await customFetch<
          Array<{
            id: string;
            name: string;
            description: string | null;
            displayOrder: number;
            isActive: boolean;
          }>
        >("/api/recruitment/candidate-sources", {
          method: "GET",
        });

        if (response.data) {
          // Sort by displayOrder and filter active sources
          const sortedSources = response.data
            .filter((source) => source.isActive)
            .sort((a, b) => a.displayOrder - b.displayOrder);
          setCandidateSources(sortedSources);
        }
      } catch (error) {
        console.error("Error fetching candidate sources:", error);
      }
    };

    fetchCandidateSources();
  }, []);

  // Update the useEffect that loads the data
  useEffect(() => {
    const loadSavedForm = async () => {
      try {
        const data = await loadFromIDB(jobId);
        if (data?.formData && hasDifferentValues(data)) {
          setSavedData(data);
          setShowSavedDataDialog(true);
        }
      } catch (error) {
        console.error("Error loading saved form:", error);
      }
    };

    loadSavedForm();
  }, [jobId]);

  // Save form data when debounced state changes
  useEffect(() => {
    const saveForm = async () => {
      try {
        await saveToIDB(jobId, {
          formData: debouncedFormState,
          answers,
        });
      } catch (error) {
        console.error("Error saving form:", error);
      }
    };

    if (Object.values(debouncedFormState).some((val) => val !== "")) {
      saveForm();
    }
  }, [debouncedFormState, answers, jobId]);

  // Update form state handler
  const handleFormChange = (
    field: keyof typeof formState,
    value: string | string[],
  ) => {
    const nextValue =
      (field === "firstName" || field === "lastName") &&
      typeof value === "string"
        ? normalizeApplicantName(value)
        : value;
    setFormState((prev) => ({
      ...prev,
      [field]: nextValue,
    }));
  };

  const handleApplyNow = () => {
    setShowFirstThingsFirst(true);
    setShowApplication(true);

    setTimeout(() => {
      const firstThingsFirstSection = document.querySelector(
        "#first-things-first-section",
      );
      firstThingsFirstSection?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  };

  const allFirstThingsFirstChecked = firstThingsFirstChecks.every(Boolean);
  const canSubmitFirstThingsFirst =
    allFirstThingsFirstChecked && hasScrolledToBottom && tncAccepted;

  const handleFirstThingsFirstSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!allFirstThingsFirstChecked) {
      setValidationError(
        "It looks like you might not be a great fit for DANA Indonesia at this time — while we encourage everyone to apply, we are looking for someone who can meet all these criteria.",
      );
      return;
    }

    if (!hasScrolledToBottom) {
      setValidationError(
        "Please scroll to the end of the Terms and Conditions before continuing.",
      );
      return;
    }

    if (!tncAccepted) {
      setValidationError(
        "You must agree to our Terms and Conditions to proceed with the application.",
      );
      return;
    }

    setValidationError(null);
    setFirstThingsFirstComplete(true);
    setShowApplication(true);
    setCurrentSection("easyApply");
    setTncAccepted(true);
    setHasScrolledToBottom(true);

    setTimeout(() => {
      const resumeSection = document.querySelector("#easy-apply-section");
      resumeSection?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  };

  const handleApplicationSubmit = async (
    e: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>,
  ) => {
    e.preventDefault();

    // Clear previous errors
    setEmailError(null);
    setSourceError(null);
    setRequiredQuestionsError(null);

    if (isApplyBlocked) {
      return;
    }

    // Get email value for validation
    const emailInput = document.getElementById("email") as HTMLInputElement;
    const emailValue = emailInput?.value?.trim() || "";

    // Validate email
    if (!emailValue) {
      setEmailError("Email is required");
      setIsSubmitting(false);
      // Scroll to email field
      emailInput?.scrollIntoView({ behavior: "smooth", block: "center" });
      emailInput?.focus();
      return;
    }

    const emailValidationMessage = getApplicantEmailAbuseMessage(emailValue);
    if (emailValidationMessage) {
      setEmailError(emailValidationMessage);
      setIsSubmitting(false);
      // Scroll to email field
      emailInput?.scrollIntoView({ behavior: "smooth", block: "center" });
      emailInput?.focus();
      return;
    }

    // Validate source only when candidate sources are available
    if (candidateSources.length > 0) {
      if (!selectedSource) {
        setSourceError("Please select how you found out about this role");
        setIsSubmitting(false);
        // Scroll to source field
        const sourceField =
          document.getElementById("source-others")?.parentElement
            ?.parentElement;
        sourceField?.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }

      // Validate "Others" text if "Others" is selected
      if (selectedSource === "others" && !otherSourceText.trim()) {
        setSourceError("Please specify how you found out about this role");
        setIsSubmitting(false);
        // Scroll to other source input
        const otherSourceInput = document.getElementById("otherSource");
        otherSourceInput?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        otherSourceInput?.focus();
        return;
      }
    }

    // Validate all required questions (general + specific) before submit
    const allRequiredQuestions = [
      ...generalQuestions,
      ...jobSpecificQuestions,
    ];
    const unansweredRequired = allRequiredQuestions.filter(
      (q) => q.isRequired && !isQuestionAnswered(q, answers),
    );
    if (unansweredRequired.length > 0) {
      setRequiredQuestionsError(
        "Please answer all required questions before submitting.",
      );
      const first = unansweredRequired[0];
      const section = generalQuestions.some((q) => q.id === first.id)
        ? "general"
        : "specific";
      const firstId = `${section}-${first.id}`;
      document.getElementById(firstId)?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return;
    }

    // Set submitting state immediately
    setIsSubmitting(true);

    // Load saved data from IDB to get the resume file and resumeId
    let savedResume: File | null = null;
    let savedResumeId: string | undefined = undefined;
    try {
      const savedData = await loadFromIDB(jobId);
      if (savedData?.resume?.file) {
        savedResume = savedData.resume.file;
      }
      if (savedData?.resumeId) {
        savedResumeId = savedData.resumeId;
      }
    } catch (error) {
      console.error("Error loading resume from IDB:", error);
    }

    // Capture form data before showing probe
    const rawLinkedin =
      (document.getElementById("linkedin") as HTMLInputElement)?.value.trim() ||
      "";
    const normalizedLinkedinUrl = rawLinkedin
      ? /^https?:\/\//i.test(rawLinkedin)
        ? rawLinkedin
        : `https://${rawLinkedin}`
      : undefined;

    const capturedFormData = {
      jobId,
      firstName: normalizeApplicantName(readFormValue("firstName")),
      lastName: normalizeApplicantName(readFormValue("lastName")),
      email: readFormValue("email"),
      phone: readFormValue("phone"),
      city: readFormValue("city"),
      province: "", // Hardcoded to empty string
      country: "indonesia", // Hardcoded to indonesia
      linkedinUrl: normalizedLinkedinUrl,
      resume:
        savedResume ||
        (document.getElementById("resume") as HTMLInputElement)?.files?.[0] ||
        null,
      dateAvailable:
        (document.getElementById("dateAvailable") as HTMLInputElement)?.value ||
        undefined,
      answers: answers,
      resumeId: savedResumeId, // Include resumeId from saved data
    };

    // Create probe config from the captured form values
    const config: Omit<ProbeRequest, "message" | "chat_history"> = {
      config_path: "config/hris_candidate_probe_v2.yaml",
      uid: "temp_uid",
      user_id: "temp_user_id",
      candidate_name: `${capturedFormData.firstName} ${capturedFormData.lastName}`,
      applied_position: jobDetails?.title || "",
      job_requirements: jobDetails?.requirements.join("\n") || "",
      candidate_cv_text: "",
      llm_language: "English",
      jobId: jobId,
      attributes: JSON.stringify({
        email: capturedFormData.email,
        phone: capturedFormData.phone,
        location: {
          city: capturedFormData.city,
          province: capturedFormData.province,
          country: capturedFormData.country,
        },
        linkedin: capturedFormData.linkedinUrl,
      }),
      resumeId: savedResumeId || "", // Use savedResumeId instead of capturedFormData.resumeId
    };

    setProbeConfig(config);
    setCapturedFormData(capturedFormData);

    // Get source value (default to HRIS when no candidate sources are configured)
    const sourceValue =
      candidateSources.length === 0
        ? "HRIS"
        : selectedSource === "others"
          ? otherSourceText.trim()
          : selectedSource
            ? candidateSources.find((s) => s.id === selectedSource)?.name || ""
            : "";

    // Prepare form data
    const formData = new FormData();
    formData.append("jobId", capturedFormData.jobId);
    formData.append("firstName", capturedFormData.firstName);
    formData.append("lastName", capturedFormData.lastName);
    formData.append("email", capturedFormData.email);
    formData.append("phone", capturedFormData.phone);
    formData.append("city", capturedFormData.city);
    formData.append("province", capturedFormData.province);
    formData.append("country", capturedFormData.country);

    if (capturedFormData.linkedinUrl) {
      formData.append("linkedinUrl", capturedFormData.linkedinUrl);
    }

    if (sourceValue) {
      formData.append("source", sourceValue);
    }

    if (capturedFormData.resume) {
      formData.append("resume", capturedFormData.resume);
    }

    if (capturedFormData.dateAvailable) {
      formData.append("dateAvailable", capturedFormData.dateAvailable);
    }

    // Add resumeId to form data if available
    if (savedResumeId) {
      formData.append("resumeId", savedResumeId);
    }

    // Format and validate answers
    const validAnswers = answers.filter((answer) => {
      if (!answer.questionId || !answer.answer?.type || !answer.answer?.value) {
        return false;
      }

      const { type, value } = answer.answer;
      if (!type || value === undefined || value === null) {
        return false;
      }

      if (type === "checkbox" && !Array.isArray(value)) {
        return false;
      }

      if ((type === "text" || type === "option") && typeof value !== "string") {
        return false;
      }

      return true;
    });

    formData.append("answers", JSON.stringify(validAnswers));
    // probeResult / chatHistory / audioUrlHistory are not sent on apply;
    // orchestrator ignores them and interview flow writes the real values later.

    // Store the form data for submission
    setFormDataToSubmit(formData);

    // If probe is enabled, show probe selection dialog
    if (isProbe) {
      setShowProbeSelection(true);
      setIsSubmitting(false); // Reset submitting state
      return; // Exit early, the probe selection will handle the next step
    }

    // Mark application as completed only when actually submitting
    setApplicationCompleted(true);

    // Call handleFinalSubmit to process the submission
    // Pass the form data directly to avoid timing issues with state updates
    handleFinalSubmit(formData);
  };

  const handleResumeUpload = async (file: File) => {
    try {
      // Validate filename for unsafe characters
      if (!validateFileName(file.name)) {
        toast({
          title: "Invalid Filename",
          description:
            "The filename contains unsafe characters. Please rename your file and try again.",
          variant: "destructive",
          duration: 3000,
        });
        return;
      }

      // Check file extension
      const fileExtension = file.name.split(".").pop()?.toLowerCase();
      if (fileExtension !== "pdf") {
        setShowResumeUploadError(true);
        return;
      }

      // Check file size (5MB = 5 * 1024 * 1024 bytes)
      if (file.size > 5 * 1024 * 1024) {
        setShowResumeUploadError(true);
        return;
      }

      // Validate actual file content type
      const isValidPDF = await validateFileType(file);
      if (!isValidPDF) {
        setShowResumeUploadError(true);
        return;
      }

      const timestamp = new Date().getTime();
      const newFileName = `resume_${timestamp}.pdf`;
      const renamedFile = new File([file], newFileName, {
        type: "application/pdf",
        lastModified: new Date().getTime(),
      });

      setIsParsingResume(true);
      // setResumeFile(renamedFile);
      setUploadedFileName(file.name);

      // Update the file input in the application form
      const resumeInput = document.getElementById("resume") as HTMLInputElement;
      if (resumeInput) {
        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(file); // Use original file name for display
        resumeInput.files = dataTransfer.files;
      }

      // Save to IDB in a single transaction
      await saveToIDB(jobId, {
        formData: formState,
        answers,
        resume: {
          file: renamedFile,
          fileName: file.name,
        },
      });

      // After successful IDB save, proceed with parsing
      const formData = new FormData();
      formData.append("resume", renamedFile);

      const response = await fetch(
        `${env.NEXT_PUBLIC_EXTERNAL_API_URL}/api/career/parse-resume`,
        {
          method: "POST",
          body: formData,
          credentials: "include", // Send session cookie
        },
      );

      if (!response.ok) {
        throw new Error("Failed to parse resume");
      }

      const parsedData = await response.json();

      if (parsedData.status === 200) {
        // Get the parsed resume data from the nested response
        const resumeData = parsedData.data.response;

        // Extract resumeId if available
        const resumeId = parsedData.data.response.resumeId;

        // Save resumeId to IDB if it exists
        if (resumeId) {
          await saveToIDB(jobId, {
            formData: formState,
            answers,
            resume: {
              file: renamedFile,
              fileName: file.name,
            },
            resumeId: resumeId,
          });
        }

        // Map the parsed fields to form state fields
        const fieldMapping: Record<string, keyof typeof formState> = {
          firstName: "firstName",
          lastName: "lastName",
          email: "email",
          phone: "phone",
          city: "city",
          province: "province",
          country: "country",
          linkedin: "linkedin",
        };

        // Create new form state with parsed data
        const newFormState = { ...formState };
        Object.entries(resumeData).forEach(([field, value]) => {
          const mappedField = fieldMapping[field];
          if (mappedField && typeof value === "string") {
            newFormState[mappedField] =
              mappedField === "firstName" || mappedField === "lastName"
                ? normalizeApplicantName(value)
                : value;
          }
        });

        // Update form state with parsed data
        setFormState(newFormState);

        // Move to application section
        setCurrentSection("application");

        // Scroll to application section
        setTimeout(() => {
          const applicationSection = document.querySelector(
            "#application-section",
          );
          applicationSection?.scrollIntoView({ behavior: "smooth" });
        }, 100);

        toast({
          title: "Resume Parsed Successfully",
          description:
            "Your resume has been processed and the form has been filled automatically.",
          variant: "default",
          duration: 3000,
        });
      }
    } catch (error) {
      console.error("Error handling resume:", error);
      toast({
        title: "Error",
        description:
          "There was an error processing your resume. Please try again.",
        variant: "destructive",
        duration: 3000,
      });
    } finally {
      setIsParsingResume(false);
    }
  };

  const handlePostponeSubmission = async () => {
    if (!formDataToSubmit) {
      console.error("No form data available for submission");
      return;
    }

    if (isApplyBlocked) {
      return;
    }

    setInterviewPreferenceLoading(true);
    setIsSubmitting(true);

    // Small delay to ensure loading state is visible
    await new Promise((resolve) => setTimeout(resolve, 100));

    try {
      // Add interviewPreference to form data
      formDataToSubmit.append("interviewPreference", "POSTPONE");

      const response = await fetch(
        `${env.NEXT_PUBLIC_EXTERNAL_API_URL}/api/career/apply`,
        {
          method: "POST",
          body: formDataToSubmit,
          credentials: "include", // CRITICAL: Send session cookie with request
          headers: {
            "X-CSRF-Token": csrfToken,
          },
        },
      );

      if (response.ok) {
        // Clear the saved form data from IDB
        try {
          const db = await initDB();
          const transaction = db.transaction(["forms"], "readwrite");
          const store = transaction.objectStore("forms");
          await store.delete(jobId);
        } catch (error) {
          console.error("Error clearing saved form:", error);
        }

        // Mark application as completed
        setApplicationCompleted(true);

        toast({
          title: "Application Submitted",
          description:
            "Your application has been successfully submitted. You can complete the interview later.",
          variant: "default",
          duration: 3000,
        });

        router.push(`/jobs/${jobId}/thank-you`);
      } else {
        const errorData = (await response.json()) as CSRFError;

        if (response.status === 401) {
          switch (errorData.message) {
            case "Missing CSRF token":
              toast({
                title: "Security Error",
                description:
                  "Security token is missing. Please refresh the page and try again.",
                variant: "destructive",
                duration: 5000,
              });
              break;
            case "Invalid or expired CSRF token":
              toast({
                title: "Session Expired",
                description:
                  "Your session has expired. Please refresh the page to continue.",
                variant: "destructive",
                duration: 5000,
              });
              break;
            default:
              toast({
                title: "Authentication Error",
                description: "Please refresh the page and try again.",
                variant: "destructive",
                duration: 5000,
              });
          }
          window.location.reload();
          return;
        }
        throw new Error(errorData.message || "Failed to submit application");
      }
    } catch (error) {
      console.error("Error submitting application:", error);
      toast({
        title: "Submission Failed",
        description:
          error instanceof Error
            ? error.message
            : "There was an error submitting your application. Please try again.",
        variant: "destructive",
        duration: 3000,
      });
    } finally {
      setInterviewPreferenceLoading(false);
      setIsSubmitting(false);
    }
  };

  const handleImmediateSubmission = async () => {
    if (!formDataToSubmit) {
      console.error("No form data available for submission");
      return;
    }

    if (isApplyBlocked) {
      return;
    }

    setInterviewPreferenceLoading(true);
    setIsSubmitting(true);

    // Small delay to ensure loading state is visible
    await new Promise((resolve) => setTimeout(resolve, 100));

    try {
      // Add interviewPreference to form data
      formDataToSubmit.append("interviewPreference", "IMMEDIATE");

      const response = await fetch(
        `${env.NEXT_PUBLIC_EXTERNAL_API_URL}/api/career/apply`,
        {
          method: "POST",
          body: formDataToSubmit,
          credentials: "include", // Send session cookie
          headers: {
            "X-CSRF-Token": csrfToken,
          },
        },
      );

      if (response.ok) {
        // Clear the saved form data from IDB
        try {
          const db = await initDB();
          const transaction = db.transaction(["forms"], "readwrite");
          const store = transaction.objectStore("forms");
          await store.delete(jobId);
        } catch (error) {
          console.error("Error clearing saved form:", error);
        }

        // Clear audio paths from IndexedDB after successful submission
        try {
          await clearAudioPaths(jobId);
        } catch (error) {
          console.error("Error clearing audio paths from IndexedDB:", error);
        }

        const interviewLink = (await response.json()).data.interviewLink;

        // Mark application as completed
        setApplicationCompleted(true);

        toast({
          title: "Application Submitted",
          description:
            "Your application has been successfully submitted. You can complete the interview now.",
          variant: "default",
          duration: 3000,
        });

        try {
          if (typeof window !== "undefined") {
            window.sessionStorage.setItem(
              "interviewEmail",
              formState?.email || "",
            );
          }
        } catch (e) {
          console.warn(
            "Failed to persist interview email in sessionStorage",
            e,
          );
        }
        router.push(`${interviewLink}`);
      } else {
        const errorData = (await response.json()) as CSRFError;

        if (response.status === 401) {
          switch (errorData.message) {
            case "Missing CSRF token":
              toast({
                title: "Security Error",
                description:
                  "Security token is missing. Please refresh the page and try again.",
                variant: "destructive",
                duration: 5000,
              });
              break;
            case "Invalid or expired CSRF token":
              toast({
                title: "Session Expired",
                description:
                  "Your session has expired. Please refresh the page to continue.",
                variant: "destructive",
                duration: 5000,
              });
              break;
            default:
              toast({
                title: "Authentication Error",
                description: "Please refresh the page and try again.",
                variant: "destructive",
                duration: 5000,
              });
          }
          window.location.reload();
          return;
        }
        throw new Error(errorData.message || "Failed to submit application");
      }
    } catch (error) {
      console.error("Error submitting application:", error);
      toast({
        title: "Submission Failed",
        description:
          error instanceof Error
            ? error.message
            : "There was an error submitting your application. Please try again.",
        variant: "destructive",
        duration: 3000,
      });
    } finally {
      setInterviewPreferenceLoading(false);
      setIsSubmitting(false);
    }
  };

  const handleProbeComplete = async (_probeResult: ProbeResult) => {
    if (!capturedFormData) {
      console.error("No form data available");
      return;
    }

    if (isApplyBlocked) {
      return;
    }

    // Hide probe component
    setShowProbe(false);

    // Get source value (default to HRIS when no candidate sources are configured)
    const sourceValue =
      candidateSources.length === 0
        ? "HRIS"
        : selectedSource === "others"
          ? otherSourceText.trim()
          : selectedSource
            ? candidateSources.find((s) => s.id === selectedSource)?.name || ""
            : "";

    const formData = new FormData();
    formData.append("jobId", capturedFormData.jobId);
    formData.append("firstName", capturedFormData.firstName);
    formData.append("lastName", capturedFormData.lastName);
    formData.append("email", capturedFormData.email);
    formData.append("phone", capturedFormData.phone);
    formData.append("city", capturedFormData.city);
    formData.append("province", capturedFormData.province);
    formData.append("country", capturedFormData.country);

    if (capturedFormData.linkedinUrl) {
      formData.append("linkedinUrl", capturedFormData.linkedinUrl);
    }

    if (sourceValue) {
      formData.append("source", sourceValue);
    }

    if (capturedFormData.resume) {
      formData.append("resume", capturedFormData.resume);
    }

    if (capturedFormData.dateAvailable) {
      formData.append("dateAvailable", capturedFormData.dateAvailable);
    }

    // Add resumeId to form data if available
    if (capturedFormData.resumeId) {
      formData.append("resumeId", capturedFormData.resumeId);
    }

    // Format and validate answers
    const validAnswers = answers.filter((answer) => {
      if (!answer.questionId || !answer.answer?.type || !answer.answer?.value) {
        return false;
      }

      const { type, value } = answer.answer;
      if (!type || value === undefined || value === null) {
        return false;
      }

      if (type === "checkbox" && !Array.isArray(value)) {
        return false;
      }

      if ((type === "text" || type === "option") && typeof value !== "string") {
        return false;
      }

      return true;
    });

    formData.append("answers", JSON.stringify(validAnswers));
    formData.append("interviewPreference", "IMMEDIATE");
    // probeResult / chatHistory not sent on apply (orchestrator sets dummy; interview saves real)

    // After probe completion, directly submit the application
    setIsSubmitting(true);

    try {
      const response = await fetch(
        `${env.NEXT_PUBLIC_EXTERNAL_API_URL}/api/career/apply`,
        {
          method: "POST",
          body: formData,
          credentials: "include", // Send session cookie
          headers: {
            "X-CSRF-Token": csrfToken,
          },
        },
      );

      if (response.ok) {
        // Clear the saved form data from IDB
        try {
          const db = await initDB();
          const transaction = db.transaction(["forms"], "readwrite");
          const store = transaction.objectStore("forms");
          await store.delete(jobId);
        } catch (error) {
          console.error("Error clearing saved form:", error);
        }

        toast({
          title: "Application Submitted",
          description: "Your application has been successfully submitted.",
          variant: "default",
          duration: 3000,
        });

        router.push(`/jobs/${jobId}/thank-you`);
      } else {
        const errorData = (await response.json()) as CSRFError;

        if (response.status === 401) {
          switch (errorData.message) {
            case "Missing CSRF token":
              toast({
                title: "Security Error",
                description:
                  "Security token is missing. Please refresh the page and try again.",
                variant: "destructive",
                duration: 5000,
              });
              break;
            case "Invalid or expired CSRF token":
              toast({
                title: "Session Expired",
                description:
                  "Your session has expired. Please refresh the page to continue.",
                variant: "destructive",
                duration: 5000,
              });
              break;
            default:
              toast({
                title: "Authentication Error",
                description: "Please refresh the page and try again.",
                variant: "destructive",
                duration: 5000,
              });
          }
          window.location.reload();
          return;
        }
        throw new Error(errorData.message || "Failed to submit application");
      }
    } catch (error) {
      console.error("Error submitting application:", error);
      toast({
        title: "Submission Failed",
        description:
          error instanceof Error
            ? error.message
            : "There was an error submitting your application. Please try again.",
        variant: "destructive",
        duration: 3000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFinalSubmit = async (formData?: FormData) => {
    const dataToSubmit = formData || formDataToSubmit;
    if (!dataToSubmit) {
      console.error("No form data available for submission");
      return;
    }

    if (isApplyBlocked) {
      return;
    }

    setIsSubmitting(true);

    // If probe is enabled, show it after TnC acceptance
    if (isProbe) {
      setShowProbe(true);
      setIsSubmitting(false); // Make sure to reset the submitting state
      return; // Exit early, the probe completion will handle the actual submission
    } else {
      // Show loading animation for non-probe submissions
      setShowSubmissionLoading(true);
      setSubmissionProgress(0);

      // Simulate progress steps
      const progressSteps = [
        { progress: 20, message: "Preparing your application..." },
        { progress: 40, message: "Validating form data..." },
        { progress: 60, message: "Uploading documents..." },
        { progress: 80, message: "Processing application..." },
        { progress: 100, message: "Finalizing submission..." },
      ];

      // Animate progress
      for (const step of progressSteps) {
        setSubmissionProgress(step.progress);
        await new Promise((resolve) => setTimeout(resolve, 300)); // 300ms delay between steps
      }
      try {
        const response = await fetch(
          `${env.NEXT_PUBLIC_EXTERNAL_API_URL}/api/career/apply`,
          {
            method: "POST",
            body: dataToSubmit,
            credentials: "include", // Send session cookie
            headers: {
              "X-CSRF-Token": csrfToken,
            },
          },
        );

        if (response.ok) {
          // Clear the saved form data from IDB
          try {
            const db = await initDB();
            const transaction = db.transaction(["forms"], "readwrite");
            const store = transaction.objectStore("forms");
            await store.delete(jobId);
          } catch (error) {
            console.error("Error clearing saved form:", error);
          }

          // Clear audio paths from IndexedDB after successful submission
          try {
            await clearAudioPaths(jobId);
          } catch (error) {
            console.error("Error clearing audio paths from IndexedDB:", error);
          }

          // Mark application as completed
          setApplicationCompleted(true);

          toast({
            title: "Application Submitted",
            description: "Your application has been successfully submitted.",
            variant: "default",
            duration: 3000,
          });

          router.push(`/jobs/${jobId}/thank-you`);
        } else {
          // Handle CSRF error
          try {
            const errorData = await response.json();
            if (
              errorData.statusCode === 403 &&
              errorData.message === "Invalid CSRF token"
            ) {
              toast({
                title: "Session Expired",
                description:
                  "Your session has expired. Please refresh the page and try again.",
                variant: "destructive",
                duration: 5000,
              });
            } else {
              throw new Error(
                errorData.message || "Failed to submit application",
              );
            }
          } catch (error) {
            toast({
              title: "Error",
              description:
                error instanceof Error
                  ? error.message
                  : "An unknown error occurred",
              variant: "destructive",
              duration: 3000,
            });
          }
        }
      } catch (error) {
        console.error("Error submitting application:", error);
        toast({
          title: "Error",
          description: "There was an error submitting your application.",
          variant: "destructive",
          duration: 3000,
        });
      } finally {
        setIsSubmitting(false);
        setShowSubmissionLoading(false);
        setSubmissionProgress(0);
      }
    }
  };

  useEffect(() => {
    const fetchJobDetails = async () => {
      if (!jobId) return;
      try {
        if (env.NEXT_PUBLIC_EXTERNAL_API_URL === undefined) return;

        const response = await customFetch<RecJob>(`/api/career/job/${jobId}`);

        const result = response as any;
        if (result.status === 200) {
          // Job
          const jobData = result.data.job as RecJob;
          setJobDetails(jobData);

          // Handle display and status conditions
          if (!jobData.isDisplayed) {
            setStatusMessage(
              "Thank you for your interest. This position is currently not available. Please check back as we will most certainly be looking for great people to join our team in the future.",
            );
          } else {
            switch (jobData.status) {
              case "DRAFT":
                setStatusMessage("DRAFT");
                setIsLoading(false);
                break;
              case "OPEN":
                setStatusMessage(null);
                setIsLoading(false);
                break;
              case "CLOSED":
                setIsLoading(false);
              case "ARCHIVED":
                setIsLoading(false);
              case "ON_HOLD":
                setStatusMessage(jobData.status);
                setIsLoading(false);
                break;
              default:
                setStatusMessage(
                  "Thank you for your interest. Unfortunately, this position is no longer available or has been filled. Please check back as we will most certainly be looking for great people to join our team in the future.",
                );
                setIsLoading(false);
            }
          }

          // Set about sections
          // const aboutSections = result.data.about;
          // const transformedData = aboutSections.reduce(
          //   (acc: { [key: string]: SectionData }, item: CmsResponseItem) => ({
          //     ...acc,
          //     [item.sectionKey]: {
          //       content: item.content,
          //       style: item.style,
          //     },
          //   }),
          //   {}
          // );
          // setCmsData(transformedData);
          setCategoryData(result.data.categoryName);
          setSectionData(
            result.data.sectionContent
              ? result.data.sectionContent
                  .replace(/\\n(?!$)/g, "\n") // Convert \n to single newline for bullet points
                  .replace(/\\n$/g, "\n\n") // Keep double newline for paragraph breaks
                  .replace(/\n{3,}/g, "\n\n") // Normalize any excessive newlines to double
              : "",
          );

          // questions
          const generalData = result.data.generalQuestions;
          // setGeneralQuestions(generalData);

          // setJobSpecificQuestions(generalData);
          const specificData = result.data.jobQuestions;
          // setJobSpecificQuestions(specificData);
          setGeneralQuestions(specificData);

          // Store the CSRF token
          if (result.data.csrfToken) {
            setCsrfToken(result.data.csrfToken);
          } else {
            // Handle missing CSRF token
            console.error("No CSRF token received from server");
            setStatusMessage(
              "Unable to load the application form securely. Please try refreshing the page.",
            );
            setIsLoading(false);
          }
        } else {
          console.error("Failed to fetch job details:", result.message);
          setStatusMessage(
            "Thank you for your interest. Unfortunately, the page you are looking for does not exist. Please check back as we will most certainly be looking for great people to join our team in the future.",
          );
          setIsLoading(false);
        }
      } catch (error) {
        console.error("Error fetching job details:", error);
        setStatusMessage(
          "An error occurred while loading this job posting. Please try again later.",
        );
        setIsLoading(false);
      }
    };

    fetchJobDetails();
  }, [jobId, env.NEXT_PUBLIC_EXTERNAL_API_URL]);

  const handleBackClick = () => {
    router.push("/#job-list");
  };

  const handleAnswerChange = (
    questionId: string,
    type: string,
    value: string | string[],
  ) => {
    setRequiredQuestionsError(null); // Clear validation error when user answers
    setAnswers((prev) => {
      const existingIndex = prev.findIndex((a) => a.questionId === questionId);

      const newAnswer = {
        questionId,
        answer: {
          type,
          value: value,
        },
      };

      const newAnswers =
        existingIndex !== -1
          ? prev.map((a, i) => (i === existingIndex ? newAnswer : a))
          : [...prev, newAnswer];

      // Save to IDB while preserving resume
      saveToIDB(jobId, {
        formData: formState,
        answers: newAnswers,
      });

      return newAnswers;
    });
  };

  const renderQuestionInput = (
    question: RecJobQuestion,
    type: "general" | "specific",
  ) => {
    const id = `${type}-${question.id}`;
    const isDraft = statusMessage === "DRAFT";
    const currentAnswer = answers.find((a) => a.questionId === question.id)
      ?.answer.value;

    switch (question.type.toLowerCase()) {
      case "text":
        return (
          <div className="relative">
            <input
              type="text"
              id={id}
              name={id}
              className={`flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring ${
                isDraft ? "opacity-70" : ""
              }`}
              value={(currentAnswer as string) || ""}
              onChange={(e) => {
                if (!isDraft) {
                  handleAnswerChange(question.id, "text", e.target.value);
                }
              }}
              placeholder="Type your answer here..."
              readOnly={isDraft}
            />
          </div>
        );

      case "option":
      case "multiple":
        return (
          <RadioGroup
            value={(currentAnswer as string) || ""}
            onValueChange={(value: string) => {
              if (!isDraft) {
                handleAnswerChange(
                  question.id,
                  question.type.toLowerCase(),
                  value,
                );
              }
            }}
            className={isDraft ? "opacity-70 pointer-events-none" : ""}
          >
            {question.possibleAnswers?.map((answer) => (
              <div key={answer} className="flex items-center space-x-2">
                <RadioGroupItem
                  value={answer}
                  id={`${id}-${answer}`}
                  disabled={isDraft}
                />
                <Label htmlFor={`${id}-${answer}`}>{answer}</Label>
              </div>
            ))}
          </RadioGroup>
        );

      case "checkbox":
        return (
          <div className={`space-y-2 ${isDraft ? "opacity-70" : ""}`}>
            {question.possibleAnswers?.map((answer) => {
              const currentValues = (currentAnswer as string[]) || [];
              return (
                <div key={answer} className="flex items-center space-x-2">
                  <Checkbox
                    id={`${id}-${answer}`}
                    disabled={isDraft}
                    checked={currentValues.includes(answer)}
                    onCheckedChange={(checked) => {
                      if (!isDraft) {
                        const currentValues =
                          (answers.find((a) => a.questionId === question.id)
                            ?.answer.value as string[]) || [];
                        const newValue = checked
                          ? [...currentValues, answer]
                          : currentValues.filter((v) => v !== answer);
                        handleAnswerChange(question.id, "checkbox", newValue);
                      }
                    }}
                  />
                  <Label htmlFor={`${id}-${answer}`}>{answer}</Label>
                </div>
              );
            })}
          </div>
        );

      case "paragraph":
      case "textarea":
        return (
          <div className="relative">
            <textarea
              id={id}
              name={id}
              className={`flex min-h-[60px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring ${
                isDraft ? "opacity-70" : ""
              }`}
              value={(currentAnswer as string) || ""}
              onChange={(e) => {
                if (!isDraft) {
                  handleAnswerChange(question.id, "textarea", e.target.value);
                }
              }}
              placeholder="Type your detailed answer here..."
              readOnly={isDraft}
            />
          </div>
        );

      default:
        return (
          <div className="relative">
            <Input
              id={id}
              value={(currentAnswer as string) || ""}
              onChange={(e) => {
                if (!isDraft) {
                  handleAnswerChange(question.id, "text", e.target.value);
                }
              }}
              className={isDraft ? "opacity-70" : ""}
              readOnly={isDraft}
            />
          </div>
        );
    }
  };

  // Helper: check if a required question has a valid answer
  const isQuestionAnswered = (
    question: RecJobQuestion,
    answersList: QuestionAnswer[],
  ): boolean => {
    const answer = answersList.find((a) => a.questionId === question.id);
    if (!answer?.answer) return false;
    const value = answer.answer.value;
    if (value === undefined || value === null) return false;
    if (typeof value === "string") return value.trim().length > 0;
    if (Array.isArray(value)) return value.length > 0;
    return false;
  };

  // Helper: get required questions that are not answered
  const getUnansweredRequiredQuestions = (
    questions: RecJobQuestion[],
    answersList: QuestionAnswer[],
  ): RecJobQuestion[] => {
    return questions.filter(
      (q) => q.isRequired && !isQuestionAnswered(q, answersList),
    );
  };

  // Modify the handleNextSection function to handle empty question sections
  const handleNextSection = (currentForm: HTMLFormElement | null) => {
    // Clear previous question validation error when moving sections
    setRequiredQuestionsError(null);

    // Basic form validation
    if (currentForm && !currentForm.checkValidity()) {
      currentForm.reportValidity();
      return;
    }

    // When leaving application section, validate candidate source
    if (currentSection === "application") {
      if (isApplyBlocked) {
        return;
      }

      if (candidateSources.length > 0) {
        if (!selectedSource) {
          setSourceError("Please select how you found out about this role");
          const sourceField =
            document.getElementById("source-others")?.parentElement
              ?.parentElement;
          sourceField?.scrollIntoView({ behavior: "smooth", block: "center" });
          return;
        }
        if (selectedSource === "others" && !otherSourceText.trim()) {
          setSourceError("Please specify how you found out about this role");
          const otherSourceInput = document.getElementById("otherSource");
          otherSourceInput?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
          otherSourceInput?.focus();
          return;
        }
        setSourceError(null); // Clear once valid when proceeding
      }
    }

    // Update states based on current section
    switch (currentSection) {
      case "easyApply":
        setCurrentSection("application");
        setTimeout(() => {
          const applicationSection = document.querySelector(
            "#application-section",
          );
          applicationSection?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }, 100);
        break;
      case "application":
        // If there are no general questions, skip to specific questions or submit
        if (generalQuestions.length === 0) {
          if (jobSpecificQuestions.length === 0) {
            // No questions at all, submit directly
            handleApplicationSubmit(new Event("submit") as any);
            return;
          } else {
            // Skip to specific questions
            setCurrentSection("specific");
            setTimeout(() => {
              const specificSection = document.querySelector(
                "#specific-questions-section",
              );
              specificSection?.scrollIntoView({
                behavior: "smooth",
                block: "start",
              });
            }, 100);
            return;
          }
        }

        // Otherwise proceed to general questions
        setCurrentSection("general");
        setTimeout(() => {
          const generalSection = document.querySelector(
            "#general-questions-section",
          );
          generalSection?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }, 100);
        break;
      case "general": {
        // Validate required general questions before proceeding
        const unansweredGeneral = getUnansweredRequiredQuestions(
          generalQuestions,
          answers,
        );
        if (unansweredGeneral.length > 0) {
          setRequiredQuestionsError(
            "Please answer all required questions before continuing.",
          );
          const firstId = `general-${unansweredGeneral[0].id}`;
          document.getElementById(firstId)?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
          return;
        }
        // First update the completion status
        setGeneralQuestionsCompleted(true);

        // If there are no specific questions, submit directly
        if (jobSpecificQuestions.length === 0) {
          handleApplicationSubmit(new Event("submit") as any);
          return;
        }

        // Otherwise proceed to specific questions
        setCurrentSection("specific");
        setTimeout(() => {
          const specificSection = document.querySelector(
            "#specific-questions-section",
          );
          specificSection?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }, 100);
        break;
      }
    }
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      handleResumeUpload(file);
    }
  };

  // Update handleContinueSavedData to restore resume file
  const handleContinueSavedData = () => {
    if (savedData) {
      // Restore form data and answers
      setFormState((prev) => ({
        ...prev,
        ...savedData.formData,
        firstName: normalizeApplicantName(
          savedData.formData.firstName ?? prev.firstName,
        ),
        lastName: normalizeApplicantName(
          savedData.formData.lastName ?? prev.lastName,
        ),
      }));
      if (savedData.answers) {
        setAnswers(savedData.answers);
      }

      // Restore resume file if exists
      if (savedData.resume?.file) {
        try {
          // setResumeFile(savedData.resume.file as File);
          setUploadedFileName(savedData.resume.fileName || "");

          // Update resume input if it exists
          const resumeInput = document.getElementById(
            "resume",
          ) as HTMLInputElement;
          if (resumeInput) {
            const dataTransfer = new DataTransfer();
            dataTransfer.items.add(savedData.resume.file as File);
            resumeInput.files = dataTransfer.files;
          }
        } catch (error) {
          console.error("Error restoring resume file:", error);
          toast({
            title: "Resume Restore Failed",
            description:
              "Could not restore the previously uploaded resume. Please upload it again.",
            variant: "destructive",
            duration: 3000,
          });
        }
      }

      // Show the application form
      setShowFirstThingsFirst(true);
      setShowApplication(true);
      setFirstThingsFirstComplete(true);

      // Determine which section to show based on saved answers
      const hasGeneralAnswers = savedData.answers.some((answer) =>
        generalQuestions.some((q) => q.id === answer.questionId),
      );
      const hasSpecificAnswers = savedData.answers.some((answer) =>
        jobSpecificQuestions.some((q) => q.id === answer.questionId),
      );

      if (hasSpecificAnswers) {
        // If has specific answers, show specific questions section
        setCurrentSection("specific");
        setApplicationCompleted(true);
        setGeneralQuestionsCompleted(true);
        setTimeout(() => {
          const specificSection = document.querySelector(
            "#specific-questions-section",
          );
          specificSection?.scrollIntoView({ behavior: "smooth" });
        }, 100);
      } else if (hasGeneralAnswers) {
        // If has general answers but no specific answers, show general questions section
        setCurrentSection("general");
        setApplicationCompleted(true);
        setTimeout(() => {
          const generalSection = document.querySelector(
            "#general-questions-section",
          );
          generalSection?.scrollIntoView({ behavior: "smooth" });
        }, 100);
      } else if (Object.values(savedData.formData).some((val) => val !== "")) {
        // If has form data but no answers, show application section
        setCurrentSection("application");
        setTimeout(() => {
          const applicationSection = document.querySelector(
            "#application-section",
          );
          applicationSection?.scrollIntoView({ behavior: "smooth" });
        }, 100);
      }
    }
    setShowSavedDataDialog(false);
  };

  // Add handler for discarding saved data
  const handleDiscardSavedData = async () => {
    try {
      const dbForms = await initDB();
      const transactionForms = dbForms.transaction(["forms"], "readwrite");
      const storeForms = transactionForms.objectStore("forms");
      await storeForms.delete(jobId);

      const dbProbes = await initDB();
      const transactionProbes = dbProbes.transaction(["probes"], "readwrite");
      const storeProbes = transactionProbes.objectStore("probes");
      await storeProbes.delete(jobId);

      // Reset form state
      setFormState(INITIAL_FORM_STATE);
      setAnswers([]);
    } catch (error) {
      console.error("Error deleting saved form:", error);
      toast({
        title: "Error",
        description: "Failed to clear saved data. Please try again.",
        variant: "destructive",
        duration: 3000,
      });
    }
    setShowSavedDataDialog(false);
  };

  // Add this effect to check for existing probe data when component mounts
  useEffect(() => {
    const checkExistingProbe = async () => {
      try {
        const db = await initDB();

        // Check if the probes store exists before attempting to access it
        if (!db.objectStoreNames.contains("probes")) {
          setHasExistingProbe(false);
          return;
        }

        const transaction = db.transaction(["probes"], "readonly");
        const store = transaction.objectStore("probes");
        const request = store.get(jobId);

        request.onsuccess = () => {
          setHasExistingProbe(!!request.result);
        };
      } catch (error) {
        console.error("Error checking for existing probe:", error);
        // Set to false as a fallback
        setHasExistingProbe(false);
      }
    };

    checkExistingProbe();
  }, [jobId]);

  // Add this effect to fetch TnC content
  useEffect(() => {
    const fetchTnC = async () => {
      try {
        if (env.NEXT_PUBLIC_EXTERNAL_API_URL === undefined) return;

        const response = await fetch(
          `${env.NEXT_PUBLIC_EXTERNAL_API_URL}/api/career/tnc`,
          {
            credentials: "include", // Send session cookie
          },
        );

        if (!response.ok) {
          throw new Error("Failed to fetch Terms and Conditions");
        }

        const result = await response.json();
        if (result.status === 200 && result.data.length > 0) {
          const tncData = result.data[0] as TnCContent;
          setTncContent(tncData.content.text);
        }
      } catch (error) {
        console.error("Error fetching Terms and Conditions:", error);
        toast({
          title: "Error",
          description: "Failed to load Terms and Conditions. Please try again.",
          variant: "destructive",
          duration: 3000,
        });
      }
    };

    fetchTnC();
  }, [env.NEXT_PUBLIC_EXTERNAL_API_URL]);

  const handleTncScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (hasReachedTncBottom(e.currentTarget)) {
      setHasScrolledToBottom(true);
    }
  };

  useEffect(() => {
    if (!showFirstThingsFirst || firstThingsFirstComplete || hasScrolledToBottom) {
      return;
    }

    const scrollEl = tncScrollRef.current;
    if (!scrollEl || !tncContent) return;

    const checkIfAlreadyAtBottom = () => {
      if (hasReachedTncBottom(scrollEl)) {
        setHasScrolledToBottom(true);
      }
    };

    checkIfAlreadyAtBottom();
    const frameId = window.requestAnimationFrame(checkIfAlreadyAtBottom);
    return () => window.cancelAnimationFrame(frameId);
  }, [
    tncContent,
    hasScrolledToBottom,
    showFirstThingsFirst,
    firstThingsFirstComplete,
  ]);

  // Modify the return statement to conditionally render the probe
  if (showProbe && probeConfig) {
    // Use interview mode gate if interviewType is AUDIO_VIDEO
    const useInterviewModeGate =
      jobDetails?.settings?.interviewType === "AUDIO_VIDEO" ||
      jobDetails?.settings?.interviewType === "VIDEO" ||
      jobDetails?.settings?.interviewType === "AUDIO";

    if (useInterviewModeGate) {
      return (
        <InterviewModeGate
          probeConfig={probeConfig}
          jobId={jobId}
          csrfToken={csrfToken}
          onComplete={handleProbeComplete}
        />
      );
    } else {
      return (
        <CandidateFeedbackProbeComponent
          probeConfig={probeConfig}
          jobId={jobId}
          onComplete={handleProbeComplete}
          csrfToken={csrfToken}
        />
      );
    }
  }

  return (
    <>
      <AlertDialog
        open={showResumeUploadError}
        onOpenChange={setShowResumeUploadError}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unable to upload CV</AlertDialogTitle>
            <AlertDialogDescription>
              The file is too large or not the right type. Please make sure your
              CV is a PDF and under 5 MB.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction className="bg-[#164a7a] hover:bg-[#2d6dad]">
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={showSavedDataDialog}
        onOpenChange={setShowSavedDataDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Continue Previous Application?</AlertDialogTitle>
            <AlertDialogDescription>
              We found a saved application from{" "}
              {savedData?.lastUpdated
                ? new Date(savedData.lastUpdated).toLocaleDateString()
                : "recently"}
              . Would you like to continue where you left off?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleDiscardSavedData}>
              Start Fresh
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleContinueSavedData}>
              Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={showProbeSelection}
        onOpenChange={(open) => {
          if (!open && !interviewPreferenceLoading) {
            setShowProbeSelection(false);
            // Restore General Questions button when user cancels so they can try again
            setGeneralQuestionsCompleted(false);
          }
        }}
      >
        <AlertDialogPortal>
          <AlertDialogOverlay className="bg-black/20" />
          <AlertDialogContent className="max-w-2xl z-[60]">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-xl font-bold text-[#164a7a]">
                Interview Preference
              </AlertDialogTitle>
              <AlertDialogDescription className="text-gray-600 mt-2">
                You have the option to complete the interview now or postpone it
                for later. If you choose to postpone, your application will be
                submitted and you can complete the interview at a more
                convenient time.
              </AlertDialogDescription>
            </AlertDialogHeader>

            <div className="mt-3 p-4 rounded-lg border bg-slate-50">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                <span className="text-sm font-semibold text-slate-800">
                  Important timings
                </span>
              </div>
              <div className="overflow-hidden rounded-md border bg-white">
                <table className="w-full text-sm">
                  <tbody>
                    <tr className="border-b">
                      <td className="px-3 py-2 text-slate-600">
                        Total interview time
                      </td>
                      <td className="px-3 py-2 font-medium text-slate-800 text-right">
                        15 minutes
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="py-6">
              <div className="space-y-4">
                <div
                  className={`p-4 border-2 rounded-lg transition-all ${
                    interviewPreferenceLoading
                      ? "cursor-not-allowed opacity-50"
                      : "cursor-pointer"
                  } ${
                    interviewPreference === "IMMEDIATE"
                      ? "border-[#164a7a] bg-blue-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                  onClick={() =>
                    !interviewPreferenceLoading &&
                    setInterviewPreference("IMMEDIATE")
                  }
                >
                  <div className="flex items-start space-x-3">
                    <input
                      type="radio"
                      name="interviewPreference"
                      value="IMMEDIATE"
                      checked={interviewPreference === "IMMEDIATE"}
                      onChange={() => setInterviewPreference("IMMEDIATE")}
                      className="mt-1"
                      disabled={interviewPreferenceLoading}
                    />
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        Complete Interview Now
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        Submit your application now and start the interview
                        process immediately. We suggest finding a quiet space
                        with minimal background noise to ensure clear audio
                        recording and help you focus on the interview.
                      </p>
                    </div>
                  </div>
                </div>

                <div
                  className={`p-4 border-2 rounded-lg transition-all ${
                    interviewPreferenceLoading
                      ? "cursor-not-allowed opacity-50"
                      : "cursor-pointer"
                  } ${
                    interviewPreference === "POSTPONE"
                      ? "border-[#164a7a] bg-blue-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                  onClick={() =>
                    !interviewPreferenceLoading &&
                    setInterviewPreference("POSTPONE")
                  }
                >
                  <div className="flex items-start space-x-3">
                    <input
                      type="radio"
                      name="interviewPreference"
                      value="POSTPONE"
                      checked={interviewPreference === "POSTPONE"}
                      onChange={() => setInterviewPreference("POSTPONE")}
                      className="mt-1"
                      disabled={interviewPreferenceLoading}
                    />
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        Postpone Interview
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        Submit your application now and complete the interview
                        later at your convenience.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <AlertDialogFooter>
              <AlertDialogCancel
                onClick={() => {
                  setShowProbeSelection(false);
                  setGeneralQuestionsCompleted(false);
                }}
                disabled={interviewPreferenceLoading}
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  if (interviewPreference && !interviewPreferenceLoading) {
                    if (interviewPreference === "IMMEDIATE") {
                      handleImmediateSubmission();
                    } else {
                      // Handle postpone - submit application directly
                      handlePostponeSubmission();
                    }
                  }
                }}
                disabled={!interviewPreference || interviewPreferenceLoading || isApplyBlocked}
                className="bg-[#164a7a] hover:bg-[#2d6dad] text-white"
              >
                {interviewPreference === "IMMEDIATE"
                  ? "Start Interview"
                  : "Submit Application"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogPortal>
      </AlertDialog>

      {/* Full-screen loading overlay for interview preference submission */}
      {interviewPreferenceLoading && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]">
          <div className="bg-white rounded-lg p-8 flex flex-col items-center space-y-4">
            <Loader2 className="h-8 w-8 animate-spin text-[#164a7a]" />
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900">
                {interviewPreference === "IMMEDIATE"
                  ? "Starting Interview..."
                  : "Submitting Application..."}
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Please wait while we process your request
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Submission Loading Dialog */}
      <Dialog open={showSubmissionLoading} onOpenChange={() => {}}>
        <DialogContent className="max-w-md w-[95%] p-6">
          <DialogHeader>
            <DialogTitle className="text-center text-xl font-bold text-[#164a7a]">
              Processing Your Application
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div className="text-center">
              <Loader2 className="h-12 w-12 animate-spin text-[#164a7a] mx-auto mb-4" />
              <p className="text-gray-600 text-sm">
                {submissionProgress <= 20 && "Preparing your application..."}
                {submissionProgress > 20 &&
                  submissionProgress <= 40 &&
                  "Validating form data..."}
                {submissionProgress > 40 &&
                  submissionProgress <= 60 &&
                  "Uploading documents..."}
                {submissionProgress > 60 &&
                  submissionProgress <= 80 &&
                  "Processing application..."}
                {submissionProgress > 80 && "Finalizing submission..."}
              </p>
            </div>
            <div className="space-y-2">
              <Progress value={submissionProgress} className="h-2" />
            </div>
            <p className="text-xs text-gray-500 text-center">
              Please don't close this window while we process your application.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      <div className="min-h-screen bg-white text-black">
        <div className="max-w-4xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
          <Button variant="ghost" className="mb-4" onClick={handleBackClick}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>

          {isLoading ? (
            <JobDetailSkeleton />
          ) : !jobDetails?.isDisplayed ? (
            // Position Unavailable Card
            <Card className="mb-8" data-scroll-section>
              <CardContent className="p-8">
                <div className="text-center">
                  <h2 className="text-2xl font-bold text-[#164a7a] mb-4">
                    Position Unavailable
                  </h2>
                  <p className="text-gray-700 mb-6">{statusMessage}</p>
                  <Button
                    asChild
                    className="bg-[#164a7a] hover:bg-[#2d6dad] text-white"
                  >
                    <Link href="/career">View Open Positions</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            // Main content for displayed jobs
            <>
              {/* Job Header Card */}
              <Card className="mb-8" data-scroll-section>
                <CardHeader className="bg-gradient-to-r from-[#2d6dad] to-[#2e83e1] text-white">
                  <div className="flex justify-between items-center">
                    <div>
                      <CardTitle className="text-3xl font-bold mb-2">
                        {jobDetails?.title || "Job Title"}
                        {statusMessage && statusMessage !== "OPEN" && (
                          <span className="ml-3 text-sm bg-yellow-500 text-white px-2 py-1 rounded">
                            {statusMessage}
                          </span>
                        )}
                      </CardTitle>
                      <div className="flex items-center text-white/80">
                        <MapPin className="h-5 w-5 mr-2" />
                        <span>{jobDetails?.location || "Location"}</span>
                      </div>
                    </div>
                  </div>
                </CardHeader>
              </Card>

              {/* Content Sections */}
              <div className="space-y-8" data-scroll-section>
                {/* About Category Section */}
                <section>
                  <h2 className="text-2xl font-bold text-[#164a7a] mb-4">
                    About {categoryData || "Category"}
                  </h2>
                  <div className="prose prose-blue max-w-none">
                    <ReactMarkdown
                      components={{
                        p: ({ children }) => (
                          <p className="text-gray-700 mb-4">{children}</p>
                        ),
                        strong: ({ children }) => (
                          <strong className="font-bold text-[#164a7a]">
                            {children}
                          </strong>
                        ),
                        em: ({ children }) => (
                          <em className="italic text-gray-800">{children}</em>
                        ),
                      }}
                    >
                      {sectionData || ""}
                    </ReactMarkdown>
                  </div>
                </section>

                {/* About Role Section */}
                <section>
                  <h2 className="text-2xl font-bold text-[#164a7a] mb-4">
                    About the role
                  </h2>
                  {jobDetails?.description && (
                    <div className="prose prose-blue max-w-none">
                      <ReactMarkdown
                        components={{
                          p: ({ children }) => (
                            <p className="text-gray-700 mb-4">{children}</p>
                          ),
                          strong: ({ children }) => (
                            <strong className="font-bold text-[#164a7a]">
                              {children}
                            </strong>
                          ),
                          em: ({ children }) => (
                            <em className="italic text-gray-800">{children}</em>
                          ),
                        }}
                      >
                        {
                          jobDetails.description
                            .replace(/\\n(?!$)/g, "\n") // Convert \n to single newline for bullet points
                            .replace(/\\n$/g, "\n\n") // Keep double newline for paragraph breaks
                            .replace(/\n{3,}/g, "\n\n") // Normalize any excessive newlines to double
                        }
                      </ReactMarkdown>
                    </div>
                  )}
                  <h3 className="text-xl font-bold text-[#164a7a] mt-6 mb-4">
                    You&apos;ll be working on:
                  </h3>
                  <ul className="list-disc pl-5 space-y-2 text-gray-700">
                    {jobDetails?.responsibilities.map(
                      (responsibility, index) => (
                        <li key={index}>{responsibility.trim()}</li>
                      ),
                    )}
                  </ul>
                  <h3 className="text-xl font-bold text-[#164a7a] mt-6 mb-4">
                    Qualifications:
                  </h3>
                  <ul className="list-disc pl-5 space-y-2 text-gray-700">
                    {jobDetails?.requirements.map((requirement, index) => (
                      <li key={index}>{requirement.trim()}</li>
                    ))}
                  </ul>
                </section>
              </div>

              {/* Apply Now Card - Only show if job is open */}
              {!isLoading && jobDetails?.isDisplayed && (
                <Card className="mt-12" data-scroll-section>
                  <CardContent className="bg-gradient-to-r from-[#2d6dad] to-[#2e83e1] text-white p-6">
                    <h2 className="text-2xl font-bold mb-4">
                      We hire people, not resumes
                    </h2>
                    <Button
                      className="bg-white text-[#164a7a] hover:bg-white/90"
                      onClick={handleApplyNow}
                    >
                      Apply Now
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* First Things First Section */}
              {showFirstThingsFirst && !firstThingsFirstComplete && (
                <Card
                  className="mt-8"
                  id="first-things-first-section"
                  data-scroll-section
                >
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-6">
                      <h2 className="text-2xl font-bold text-[#164a7a]">
                        First things first
                      </h2>
                    </div>
                    <p className="text-gray-700 mb-6">
                      We want to ensure you&apos;re the type of person who will
                      thrive here so we can move you quickly through our hiring
                      process. Before completing the job application, please
                      check all the statements that apply to you below:
                    </p>
                    <form
                      onSubmit={handleFirstThingsFirstSubmit}
                      className="space-y-6"
                    >
                      <div className="space-y-3">
                        {FIRST_THINGS_FIRST_STATEMENTS.map((text, index) => (
                          <div
                            key={text}
                            className="flex items-start space-x-3"
                          >
                            <Checkbox
                              id={`checkbox-${index}`}
                              className="mt-1"
                              checked={firstThingsFirstChecks[index]}
                              onCheckedChange={(checked) => {
                                setFirstThingsFirstChecks((prev) => {
                                  const next = [...prev];
                                  next[index] = checked === true;
                                  return next;
                                });
                              }}
                            />
                            <Label
                              htmlFor={`checkbox-${index}`}
                              className="text-gray-700 leading-normal"
                            >
                              {text}
                            </Label>
                          </div>
                        ))}
                      </div>

                      <div className="space-y-4 pt-4 border-t border-gray-200">
                        <h3 className="text-xl font-bold text-[#164a7a]">
                          Terms and Conditions
                        </h3>
                        <div className="text-gray-600 italic space-y-2 text-sm md:text-base">
                          <p>
                            By submitting your information, you consent to its
                            use for processing your application, assessing your
                            suitability, and for any related or additional
                            purposes as disclosed when you give consent. Further
                            details are as per below.
                          </p>
                          <p>
                            Please also review our{" "}
                            <a
                              href="/privacy-policy"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[#164a7a] hover:underline font-medium not-italic"
                            >
                              Privacy Policy
                            </a>{" "}
                            for details on how we protect your data.
                          </p>
                          <p>
                            By clicking &quot;Submit&quot; and checking the box,
                            you confirm that you have read, understood, and
                            agree to these terms.
                          </p>
                        </div>

                        <Card>
                          <CardContent className="p-3 md:p-6">
                            <div
                              ref={tncScrollRef}
                              onScroll={handleTncScroll}
                              className="prose prose-blue max-w-none h-[40vh] md:h-[50vh] overflow-y-auto pr-2 md:pr-4 [scrollbar-width:thin] [scrollbar-color:#164a7a_#f1f5f9] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-slate-100 [&::-webkit-scrollbar-track]:rounded-lg [&::-webkit-scrollbar-thumb]:bg-[#164a7a] [&::-webkit-scrollbar-thumb]:rounded-lg [&::-webkit-scrollbar-thumb]:border-2 [&::-webkit-scrollbar-thumb]:border-slate-100"
                            >
                              {tncContent ? (
                                <div
                                  dangerouslySetInnerHTML={{
                                    __html: formatTncHtml(tncContent),
                                  }}
                                  className="text-base space-y-4"
                                />
                              ) : (
                                <p className="text-gray-500 text-sm">
                                  Loading Terms and Conditions...
                                </p>
                              )}
                            </div>
                          </CardContent>
                        </Card>

                        <div className="space-y-2">
                          <div className="flex items-start space-x-3">
                            <Checkbox
                              id="checkbox-tnc"
                              className="mt-1"
                              checked={tncAccepted}
                              disabled={!hasScrolledToBottom}
                              onCheckedChange={(checked) => {
                                if (!hasScrolledToBottom) return;
                                setTncAccepted(checked === true);
                              }}
                            />
                            <Label
                              htmlFor="checkbox-tnc"
                              className={`leading-normal ${
                                hasScrolledToBottom
                                  ? "text-gray-700"
                                  : "text-gray-400 cursor-not-allowed"
                              }`}
                            >
                              I agree to the Terms and Conditions
                            </Label>
                          </div>
                          {!hasScrolledToBottom && (
                            <p className="text-sm text-gray-500 pl-7">
                              Scroll to the end of the Terms and Conditions to
                              enable this checkbox.
                            </p>
                          )}
                        </div>
                      </div>
                      {validationError && (
                        <p className="mt-4 text-[#164a7a] font-medium bg-blue-50 p-4 rounded-lg">
                          {validationError}
                        </p>
                      )}
                      <Button
                        type="submit"
                        disabled={!canSubmitFirstThingsFirst}
                        className="w-full bg-[#164a7a] hover:bg-[#2d6dad] text-white disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Submit
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              )}

              {/* Resume Upload Section */}
              {showApplication && !statusMessage && (
                <>
                  <Card
                    className={`mt-8 transition-all duration-300 ${
                      !firstThingsFirstComplete
                        ? "opacity-50 pointer-events-none blur-[2px]"
                        : ""
                    }`}
                    id="easy-apply-section"
                    data-scroll-section
                  >
                    <CardContent className="p-8">
                      <h1 className="text-2xl font-bold text-[#164a7a] mb-4">
                        Easy Apply
                      </h1>

                      {!isProbe && (
                        <div className="flex items-center gap-2 text-[#164a7a] mb-4 p-4 bg-blue-50 rounded-lg border border-blue-100">
                          <Clock className="h-5 w-5 flex-shrink-0" />
                          <p className="font-medium">
                            This application takes about{" "}
                            {Math.ceil(
                              calculateEstimatedTime(
                                generalQuestions.length,
                                jobSpecificQuestions.length,
                                isProbe,
                              ).minutes,
                            )}{" "}
                            minutes to complete.{" "}
                            <span className="text-[#164a7a] font-semibold">
                              Don&apos;t worry
                            </span>{" "}
                            - you can save your progress and return later!
                          </p>
                        </div>
                      )}
                      <p className="text-gray-600 mb-2">
                        Upload your resume for us to autocomplete your
                        application.
                      </p>
                      <p className="text-gray-600 mb-6 text-sm italic">
                        *Note: Do not include sensitive data, such as copy of your KTP and KK.
                      </p>

                      <div
                        className={`border-2 border-dashed rounded-lg p-12 transition-colors duration-200 ${
                          isDragging
                            ? "border-[#164a7a] bg-blue-50"
                            : "border-gray-200"
                        }`}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsDragging(true);
                        }}
                        onDragEnter={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsDragging(true);
                        }}
                        onDragLeave={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsDragging(false);
                        }}
                        onDrop={handleDrop}
                      >
                        <input
                          type="file"
                          id="resume-upload"
                          className="hidden"
                          accept=".pdf"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleResumeUpload(file);
                            e.target.value = "";
                          }}
                          disabled={isParsingResume}
                        />
                        <label
                          htmlFor="resume-upload"
                          className={`cursor-pointer flex flex-col items-center justify-center ${
                            isParsingResume ? "pointer-events-none" : ""
                          }`}
                        >
                          {isParsingResume ? (
                            <div className="flex flex-col items-center gap-3 text-[#164a7a]">
                              <Upload className="h-6 w-6 animate-bounce" />
                              <span className="font-medium">
                                Processing Resume...
                              </span>
                              <p className="text-sm text-gray-500">
                                Extracting your information to simplify the
                                application process.
                              </p>
                              <div className="h-1 w-48 bg-gray-200 rounded-full overflow-hidden">
                                <div className="h-full bg-[#164a7a] animate-[loading_1.5s_ease-in-out_infinite]" />
                              </div>
                            </div>
                          ) : uploadedFileName ? (
                            <div className="space-y-4">
                              <div className="flex items-center gap-2 text-green-600">
                                <Upload className="h-6 w-6" />
                                <span className="font-medium">
                                  Uploaded: {uploadedFileName}
                                </span>
                              </div>
                              <p className="text-sm text-gray-500">
                                Click to upload a different file
                              </p>
                              {currentSection === "easyApply" && (
                                <div className="p-4 bg-green-50 rounded-lg">
                                  <p className="text-green-700">
                                    We&apos;ve filled the fields with your
                                    resume details. Please review and confirm
                                    below.
                                  </p>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="space-y-6 text-center">
                              <Upload
                                className={`h-12 w-12 mx-auto transition-colors duration-200 ${
                                  isDragging
                                    ? "text-[#2d6dad]"
                                    : "text-[#164a7a]"
                                }`}
                              />
                              <div>
                                <p
                                  className={`text-2xl font-medium mb-4 transition-colors duration-200 ${
                                    isDragging
                                      ? "text-[#2d6dad]"
                                      : "text-gray-700"
                                  }`}
                                >
                                  {isDragging
                                    ? "Drop your file here"
                                    : "Drop your resume here"}
                                </p>
                                <p className="text-gray-500 mb-4">or</p>
                                <Button
                                  type="button"
                                  variant="outline"
                                  className="border-[#164a7a] text-[#164a7a] hover:bg-[#164a7a] hover:text-white font-medium px-8"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    document
                                      .getElementById("resume-upload")
                                      ?.click();
                                  }}
                                >
                                  Select file
                                </Button>
                              </div>
                              <p className="text-sm text-gray-500">
                                Supported format: PDF only (max 5MB)
                              </p>
                            </div>
                          )}
                        </label>
                      </div>
                    </CardContent>
                  </Card>
                </>
              )}

              {/* Application Form Section */}
              {showApplication && (
                <>
                  <Card
                    className={`mt-8 transition-all duration-300 ${
                      !firstThingsFirstComplete ||
                      currentSection === "easyApply"
                        ? "opacity-50 pointer-events-none blur-[2px]"
                        : ""
                    }`}
                    id="application-section"
                    data-scroll-section
                  >
                    <CardContent className="p-6">
                      <div className="flex justify-between items-center mb-6">
                        <h2 className="text-2xl font-bold text-[#164a7a]">
                          Application
                        </h2>
                      </div>
                      <form
                        id="application-form"
                        className="space-y-6"
                        onSubmit={(e) => {
                          e.preventDefault();
                          const form = e.currentTarget;

                          // Clear previous email error
                          setEmailError(null);

                          // Get email value for validation
                          const emailInput = document.getElementById(
                            "email",
                          ) as HTMLInputElement;
                          const emailValue = emailInput?.value?.trim() || "";

                          // Validate email
                          if (!emailValue) {
                            setEmailError("Email is required");
                            emailInput?.scrollIntoView({
                              behavior: "smooth",
                              block: "center",
                            });
                            emailInput?.focus();
                            return;
                          }

                          const emailValidationMessage =
                            getApplicantEmailAbuseMessage(emailValue);
                          if (emailValidationMessage) {
                            setEmailError(emailValidationMessage);
                            emailInput?.scrollIntoView({
                              behavior: "smooth",
                              block: "center",
                            });
                            emailInput?.focus();
                            return;
                          }

                          // Validate form
                          if (!form.checkValidity()) {
                            form.reportValidity();
                            return;
                          }

                          // Call handleNextSection directly
                          handleNextSection(form);
                        }}
                      >
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label
                              htmlFor="firstName"
                              className="text-gray-700"
                            >
                              First Name
                              <span className="text-red-500 ml-1">*</span>
                            </Label>
                            <Input
                              id="firstName"
                              required
                              className={`border-gray-300 ${
                                nameAbuseMessage
                                  ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                                  : ""
                              }`}
                              value={formState.firstName}
                              onChange={(e) =>
                                handleFormChange("firstName", e.target.value)
                              }
                              aria-invalid={nameAbuseMessage ? "true" : "false"}
                              aria-describedby={
                                nameAbuseMessage ? "name-error" : undefined
                              }
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="lastName" className="text-gray-700">
                              Last Name
                            </Label>
                            <Input
                              id="lastName"
                              required
                              className={`border-gray-300 ${
                                nameAbuseMessage
                                  ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                                  : ""
                              }`}
                              value={formState.lastName}
                              onChange={(e) =>
                                handleFormChange("lastName", e.target.value)
                              }
                              aria-invalid={nameAbuseMessage ? "true" : "false"}
                              aria-describedby={
                                nameAbuseMessage ? "name-error" : undefined
                              }
                            />
                          </div>
                        </div>
                        {nameAbuseMessage && (
                          <p
                            id="name-error"
                            className="text-red-500 text-sm mt-1"
                          >
                            {nameAbuseMessage}
                          </p>
                        )}
                        <div className="space-y-2">
                          <Label htmlFor="email" className="text-gray-700">
                            Email
                            <span className="text-red-500 ml-1">*</span>
                          </Label>
                          <Input
                            id="email"
                            type="email"
                            required
                            className={`border-gray-300 ${
                              emailAbuseMessage || emailError
                                ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                                : ""
                            }`}
                            value={formState.email}
                            onChange={(e) => {
                              handleFormChange("email", e.target.value);
                              // Clear error when user starts typing
                              if (emailError) {
                                setEmailError(null);
                              }
                            }}
                            aria-invalid={
                              emailAbuseMessage || emailError ? "true" : "false"
                            }
                            aria-describedby={
                              emailAbuseMessage || emailError
                                ? "email-error"
                                : undefined
                            }
                          />
                          {(emailAbuseMessage || emailError) && (
                            <p
                              id="email-error"
                              className="text-sm text-red-600 mt-1"
                              role="alert"
                            >
                              {emailAbuseMessage || emailError}
                            </p>
                          )}
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="phone" className="text-gray-700">
                            Phone
                            <span className="text-red-500 ml-1">*</span>
                          </Label>
                          <Input
                            id="phone"
                            type="tel"
                            required
                            className="border-gray-300"
                            value={formState.phone}
                            onChange={(e) =>
                              handleFormChange("phone", e.target.value)
                            }
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="city" className="text-gray-700">
                            City
                            <span className="text-red-500 ml-1">*</span>
                          </Label>
                          <Input
                            id="city"
                            type="text"
                            required
                            className="border-gray-300"
                            value={formState.city}
                            onChange={(e) =>
                              handleFormChange("city", e.target.value)
                            }
                          />
                        </div>
                        <div className="space-y-2 hidden">
                          <Label
                            htmlFor="dateAvailable"
                            className="text-gray-700"
                          >
                            Date Available
                          </Label>
                          <Input
                            id="dateAvailable"
                            type="date"
                            className="border-gray-300"
                            min={
                              new Date(Date.now() + 86400000)
                                .toISOString()
                                .split("T")[0]
                            }
                            value={formState.dateAvailable}
                            onChange={(e) =>
                              handleFormChange("dateAvailable", e.target.value)
                            }
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="linkedin" className="text-gray-700">
                            LinkedIn Profile
                          </Label>
                          <Input
                            id="linkedin"
                            className="border-gray-300"
                            type="text"
                            inputMode="url"
                            placeholder="www.linkedin.com/in/your-profile"
                            pattern="(https?:\/\/)?(www\.)?linkedin\.com\/.*"
                            title="Please enter a valid LinkedIn URL (e.g., www.linkedin.com/in/your-profile)"
                            value={formState.linkedin}
                            onChange={(e) =>
                              handleFormChange("linkedin", e.target.value)
                            }
                          />
                        </div>
                        {candidateSources.length > 0 && (
                          <div className="space-y-3">
                            <Label className="text-gray-700">
                              How did you find out about this role?
                              <span className="text-red-500 ml-1">*</span>
                            </Label>
                            <RadioGroup
                              value={selectedSource}
                              onValueChange={(value) => {
                                setSelectedSource(value);
                                setSourceError(null); // Clear error when selection changes
                                if (value !== "others") {
                                  setOtherSourceText("");
                                }
                              }}
                              className="space-y-3"
                            >
                              {candidateSources.map((source) => (
                                <div
                                  key={source.id}
                                  className="flex items-center space-x-2"
                                >
                                  <RadioGroupItem
                                    value={source.id}
                                    id={`source-${source.id}`}
                                  />
                                  <Label
                                    htmlFor={`source-${source.id}`}
                                    className="text-gray-700 font-normal cursor-pointer"
                                  >
                                    {source.name}
                                  </Label>
                                </div>
                              ))}
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem
                                  value="others"
                                  id="source-others"
                                />
                                <Label
                                  htmlFor="source-others"
                                  className="text-gray-700 font-normal cursor-pointer"
                                >
                                  Others
                                </Label>
                              </div>
                            </RadioGroup>
                            {selectedSource === "others" && (
                              <Input
                                id="otherSource"
                                className={`border-gray-300 mt-2 ml-6 ${
                                  sourceError && selectedSource === "others"
                                    ? "border-red-500"
                                    : ""
                                }`}
                                type="text"
                                placeholder="Please specify"
                                value={otherSourceText}
                                onChange={(e) => {
                                  setOtherSourceText(e.target.value);
                                  setSourceError(null); // Clear error when typing
                                }}
                              />
                            )}
                            {sourceError && (
                              <p className="text-red-500 text-sm mt-1 ml-6">
                                {sourceError}
                              </p>
                            )}
                          </div>
                        )}
                        {!applicationCompleted && (
                          <Button
                            type="submit"
                            size="lg"
                            className="w-full bg-[#164a7a] hover:bg-[#2d6dad] text-white relative z-0"
                            disabled={isSubmitting || isApplyBlocked}
                          >
                            {isSubmitting ? (
                              <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Submitting...
                              </>
                            ) : generalQuestions.length === 0 &&
                              jobSpecificQuestions.length === 0 ? (
                              <>
                                {isProbe
                                  ? "Continue to Application"
                                  : "Submit Application"}
                                <ArrowRight className="ml-2 h-4 w-4" />
                              </>
                            ) : hasExistingProbe ? (
                              <>
                                Continue
                                <ArrowRight className="ml-2 h-4 w-4" />
                              </>
                            ) : (
                              <>
                                Next
                                <ArrowRight className="ml-2 h-4 w-4" />
                              </>
                            )}
                          </Button>
                        )}
                      </form>
                    </CardContent>
                  </Card>

                  {/* General Questions Section - Show in DRAFT mode */}
                  {generalQuestions?.length > 0 && (
                    <Card
                      className={`mt-8 transition-all duration-300 ${
                        !firstThingsFirstComplete ||
                        currentSection === "easyApply" ||
                        currentSection === "application"
                          ? "opacity-50 pointer-events-none blur-[2px]"
                          : ""
                      }`}
                      id="general-questions-section"
                      data-scroll-section
                    >
                      <CardContent className="p-6">
                        <div className="flex justify-between items-center mb-6">
                          <h2 className="text-2xl font-bold text-[#164a7a]">
                            General Questions
                          </h2>
                        </div>
                        {requiredQuestionsError && (
                          <p className="text-red-500 text-sm mb-4" role="alert">
                            {requiredQuestionsError}
                          </p>
                        )}
                        <form
                          className="space-y-6"
                          onSubmit={(e) => {
                            e.preventDefault();
                            if (statusMessage !== "DRAFT") {
                              handleNextSection(e.currentTarget);
                            }
                          }}
                        >
                          {generalQuestions?.map((question) => (
                            <div key={question.id} className="space-y-2">
                              <Label
                                htmlFor={question.id}
                                className="text-gray-700"
                              >
                                {question.question}
                                {question.isRequired && (
                                  <span className="text-red-500 ml-1">*</span>
                                )}
                              </Label>
                              <div className="mt-2">
                                {renderQuestionInput(question, "general")}
                              </div>
                            </div>
                          ))}

                          {!generalQuestionsCompleted &&
                            statusMessage !== "DRAFT" && (
                              <Button
                                type="submit"
                                className="w-full bg-[#164a7a] hover:bg-[#2d6dad] text-white"
                                disabled={isSubmitting || isApplyBlocked}
                              >
                                {isSubmitting ? (
                                  <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Submitting...
                                  </>
                                ) : jobSpecificQuestions?.length > 0 ? (
                                  <>
                                    Next
                                    <ArrowRight className="ml-2 h-4 w-4" />
                                  </>
                                ) : hasExistingProbe ? (
                                  <>
                                    Continue
                                    <ArrowRight className="ml-2 h-4 w-4" />
                                  </>
                                ) : (
                                  <>
                                    {isProbe
                                      ? "Continue to Application"
                                      : "Submit Application"}
                                    <ArrowRight className="ml-2 h-4 w-4" />
                                  </>
                                )}
                              </Button>
                            )}
                        </form>
                      </CardContent>
                    </Card>
                  )}

                  {/* Job Specific Questions Section - Show in DRAFT mode */}
                  {jobSpecificQuestions?.length > 0 && (
                    <Card
                      className={`mt-8 transition-all duration-300 ${
                        !firstThingsFirstComplete ||
                        currentSection !== "specific"
                          ? "opacity-50 pointer-events-none blur-[2px]"
                          : ""
                      }`}
                      id="specific-questions-section"
                      data-scroll-section
                    >
                      <CardContent
                        className={`p-6 ${
                          currentSection !== "specific" &&
                          !generalQuestionsCompleted
                            ? "opacity-50 pointer-events-none blur-[2px]"
                            : ""
                        }`}
                      >
                        <div className="flex justify-between items-center mb-6">
                          <h2 className="text-2xl font-bold text-[#164a7a]">
                            Job Specific Questions
                          </h2>
                        </div>
                        {requiredQuestionsError && (
                          <p className="text-red-500 text-sm mb-4" role="alert">
                            {requiredQuestionsError}
                          </p>
                        )}
                        <form
                          className="space-y-6"
                          onSubmit={handleApplicationSubmit}
                        >
                          {jobSpecificQuestions?.map((question) => (
                            <div key={question.id} className="space-y-2">
                              <Label
                                htmlFor={question.id}
                                className="text-gray-700"
                              >
                                {question.question}
                                {question.isRequired && (
                                  <span className="text-red-500 ml-1">*</span>
                                )}
                              </Label>
                              <div className="mt-2">
                                {renderQuestionInput(question, "specific")}
                              </div>
                            </div>
                          ))}
                        </form>
                      </CardContent>
                    </Card>
                  )}

                  {/* Submit Application Button - Only disabled in DRAFT mode */}
                  {currentSection === "specific" && (
                    <div className="mt-8">
                      <Button
                        onClick={handleApplicationSubmit}
                        size="lg"
                        className="w-full bg-[#164a7a] hover:bg-[#2d6dad] text-white"
                        disabled={isSubmitting || isApplyBlocked}
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Submitting...
                          </>
                        ) : hasExistingProbe ? (
                          <>
                            Continue
                            <ArrowRight className="ml-2 h-4 w-4" />
                          </>
                        ) : (
                          <>
                            {isProbe
                              ? "Continue to Application"
                              : "Submit Application"}
                            <ArrowRight className="ml-2 h-4 w-4" />
                          </>
                        )}
                      </Button>
                    </div>
                  )}

                  {/* Status Notice */}
                  {(statusMessage === "DRAFT" ||
                    jobDetails?.status !== "OPEN") && (
                    <Card className="mt-4">
                      <CardContent className="p-4 bg-yellow-50">
                        <p className="text-yellow-800">
                          {statusMessage === "DRAFT"
                            ? "This job posting is currently in draft status. You can preview the application process, but submissions are disabled."
                            : "This position is no longer accepting applications at this time. Please check back later or explore other opportunities."}
                        </p>
                      </CardContent>
                    </Card>
                  )}
                </>
              )}
            </>
          )}
        </div>
        <style jsx global>{`
          @keyframes loading {
            0% {
              transform: translateX(-100%);
            }
            50% {
              transform: translateX(100%);
            }
            100% {
              transform: translateX(-100%);
            }
          }
        `}</style>
      </div>
    </>
  );
}
